import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { Spinner } from '../../Loaders/Loaders'
import EntitySelectionList from '../../WorkviewManager/PaperProcessModel/EntitySelectionList'
import OrdersFormList from './OrdersFormList'
import * as api from '../../../requests'
import {
  SET_ORDER_DETAILS,
  SET_SEARCH_RESULTS,
  SET_RECORD_SEARCH_QUERY,
  SET_STAFF_DRAFT,
  defaultRecordSearchQuery,
} from '../Workview2Reducer'
import { ContextButtonControl } from '../../WorkviewManager/PaperProcessModel/PaperProcessModal'
import WVMNotes from '../../Notes/WVMNotes'
import { useRequestData } from '../../../Utilities/hooks'
import { useHistory } from 'react-router-dom'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import {
  FilingSourceTypeIds,
  getFilingSourceTypeIdFromRecordSourceTypeId,
} from '../../../Utilities/Utilities'

const OrdersPaperProcessModal = ({
  navToCorrespondence,
  closePaperModalUnlockItem,
  refreshView,
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { selectedRow, orderDetails } = useSelector((state) => state.workview2, shallowEqual)
  const { activeSourceTypes } = useSelector((state) => state.app, shallowEqual)
  const [showNoPaymentConfirm, setShowNoPaymentConfirm] = useState(false)
  const [loadingNoPaymentData, setLoadingNoPaymentData] = useState(false)
  const [selectedSourceType, setSelectedSourceType] = useState(0)
  const [sourceTypeOptions, setSourceTypeOptions] = useState([])
  const [triggerClear, setTriggerClear] = useState(false)
  const orderFilingXrefs = useRequestData(api.GET_ORDERS_XREF, [], [])

  useEffect(() => {
    if (orderFilingXrefs.length > 0) {
      const orderXrefSourceTypes = new Set()
      orderFilingXrefs.forEach((xref) => orderXrefSourceTypes.add(xref.SourceTypeId))
      const options = []
      orderXrefSourceTypes.forEach((id) =>
        options.push({
          label: activeSourceTypes.find((type) => type.SourceTypeId === id).ModuleName,
          value: id,
        })
      )
      setSourceTypeOptions(options.sort((a, b) => (a.label > b.label ? 1 : -1)))
      if (selectedSourceType !== options[0].value) {
        setSelectedSourceType(options[0].value)
        dispatch({
          type: SET_ORDER_DETAILS,
          orderDetails: {
            SourceTypeId: getFilingSourceTypeIdFromRecordSourceTypeId(options[0].value),
          },
        })
      }
    }
  }, [orderFilingXrefs, activeSourceTypes])

  useEffect(() => {
    setLoadingNoPaymentData(true)
    api.DOES_IMAGE_ENVELOPE_HAVE_PAYMENT(
      selectedRow.ImageEnvelopeId,
      (hasPayment) => {
        setLoadingNoPaymentData(false)
        setShowNoPaymentConfirm(!hasPayment)
      },
      (err) => {
        setLoadingNoPaymentData(false)
        setShowNoPaymentConfirm(false)
        console.error(err.Message)
      }
    )
  }, [selectedRow.ImageEnvelopeId])

  useEffect(() => {
    const { SelectedFormDefinitionId, SelectedFormStaffDraft, RecordToAmend } = selectedRow
    if (selectedSourceType === 0 && SelectedFormDefinitionId) {
      // if we have SelectedFormDefinitionID and it is for the ALL sourceType -> go to wizard
      const redirect = `/office/workview/process/${
        SelectedFormStaffDraft ? 'draft/' : ''
      }${SelectedFormDefinitionId}`
      history.push(redirect)
    } else if (RecordToAmend && SelectedFormDefinitionId) {
      // if we have the recordToAmend and FormDefinitionID -> go to wizard
      const type =
        orderDetails.SourceTypeId === FilingSourceTypeIds.Trademark
          ? 'TRADEMARK'
          : orderDetails.SourceTypeId === FilingSourceTypeIds.Business
          ? 'BUSINESS'
          : orderDetails.SourceTypeId === FilingSourceTypeIds.Charity
          ? 'charity'
          : orderDetails.SourceTypeId === FilingSourceTypeIds.Contractor
          ? 'contractor'
          : orderDetails.SourceTypeId === FilingSourceTypeIds.Lobbyist
          ? 'lobbyist'
          : 'ALL'
      const redirect = `/office/workview/${
        SelectedFormStaffDraft ? 'draft/' : ''
      }process/${SelectedFormDefinitionId}?category=${type}&sourceId=${RecordToAmend.SourceID}`
      history.push(redirect)
    }
  }, [selectedRow, orderFilingXrefs, selectedSourceType, orderDetails])

  useEffect(() => {
    return () => {
      dispatch({
        type: SET_SEARCH_RESULTS,
        results: [],
      })
      dispatch({
        type: SET_RECORD_SEARCH_QUERY,
        query: defaultRecordSearchQuery,
      })
      dispatch({ type: SET_STAFF_DRAFT, staffDraft: null })
    }
  }, [])

  const showFormSelection = useMemo(() => {
    return +selectedSourceType === 0 || selectedRow.RecordToAmend
  }, [selectedSourceType, selectedRow])

  const showEntitySelection = useMemo(() => {
    return +selectedSourceType !== 0 && !selectedRow.RecordToAmend
  }, [selectedRow, selectedSourceType])

  return (
    <div className="wvm-modal">
      <div className="flex-column-wrapper paper-process-container">
        {loadingNoPaymentData && <Spinner className="page-loader" themeColor="neutral" />}
        <br />
        <SelectBox
          label="Source Type"
          noPlaceholder
          value={selectedSourceType}
          onChange={(val) => {
            setSelectedSourceType(val)
            setTriggerClear(true)
            dispatch({
              type: SET_ORDER_DETAILS,
              orderDetails: { SourceTypeId: getFilingSourceTypeIdFromRecordSourceTypeId(+val) },
            })
          }}
          options={sourceTypeOptions}
        />
        {showFormSelection && (
          <OrdersFormList
            orderFilingXrefs={orderFilingXrefs}
            selectedSourceType={selectedSourceType}
          />
        )}
        {showEntitySelection && (
          <EntitySelectionList triggerClear={triggerClear} setTriggerClear={setTriggerClear} />
        )}
      </div>
      {showNoPaymentConfirm && (
        <div className="no-payment-warning">No Payment found in Image Envelope.</div>
      )}
      <WVMNotes />
      <ContextButtonControl
        navToCorrespondence={navToCorrespondence}
        refreshView={refreshView}
        closePaperModalUnlockItem={closePaperModalUnlockItem}
      />
    </div>
  )
}

export default OrdersPaperProcessModal
